import {
  PreventionsByYearPerMonth,
  MONTH_ORDER,
  ReportFilters,
} from '../../../types/types';

export interface PreventionMetrics {
  ytdHouseholds: number;
  ytdIndividuals: number;
  /** Only populated when filters.mode is 'month' or 'both'. */
  mtdHouseholds: number | null;
  mtdIndividuals: number | null;
  /** Percent change vs. the same YTD cutoff last year. Null if no prior-year data. */
  householdsYoyPct: number | null;
  individualsYoyPct: number | null;
}

function cutoffIndex(filters: ReportFilters): number {
  if (filters.month) return MONTH_ORDER.indexOf(filters.month);
  return MONTH_ORDER.length - 1;
}

function sumThroughCutoff(
  yearData: PreventionsByYearPerMonth[string] | undefined,
  cutoff: number,
  field: 'total_evictions_households' | 'total_evictions_individuals',
): number {
  if (!yearData) return 0;
  return MONTH_ORDER.slice(0, cutoff + 1).reduce((total, month) => {
    return total + (yearData[month]?.[field] ?? 0);
  }, 0);
}

function pctChange(current: number, prior: number): number | null {
  if (prior === 0) return null;
  return ((current - prior) / prior) * 100;
}

/**
 * Now includes MTD alongside YTD (there was room on the page, per your
 * request) — same includesMonth convention as eviction numbers: a month
 * value only appears when mode is 'month' or 'both'.
 */
export function computePreventionMetrics(
  data: PreventionsByYearPerMonth,
  filters: ReportFilters,
): PreventionMetrics {
  const cutoff = cutoffIndex(filters);
  const yearData = data[String(filters.year)];
  const priorYearData = data[String(filters.year - 1)];

  const ytdHouseholds = sumThroughCutoff(yearData, cutoff, 'total_evictions_households');
  const ytdIndividuals = sumThroughCutoff(yearData, cutoff, 'total_evictions_individuals');

  const priorYtdHouseholds = sumThroughCutoff(priorYearData, cutoff, 'total_evictions_households');
  const priorYtdIndividuals = sumThroughCutoff(priorYearData, cutoff, 'total_evictions_individuals');

  const includesMonth = filters.mode === 'month' || filters.mode === 'both';
  const mtdHouseholds = includesMonth && filters.month
    ? yearData?.[filters.month]?.total_evictions_households ?? 0
    : null;
  const mtdIndividuals = includesMonth && filters.month
    ? yearData?.[filters.month]?.total_evictions_individuals ?? 0
    : null;

  return {
    ytdHouseholds,
    ytdIndividuals,
    mtdHouseholds,
    mtdIndividuals,
    householdsYoyPct: pctChange(ytdHouseholds, priorYtdHouseholds),
    individualsYoyPct: pctChange(ytdIndividuals, priorYtdIndividuals),
  };
}